/**
 * MCP 日志模块
 * 
 * 统一的日志接口，支持不同级别和格式化输出
 * MCP 服务器通过 stderr 输出日志，避免污染 stdout
 * 
 * @module core/logger
 */

/**
 * 日志级别
 */
export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
    SILENT = 4
}

/**
 * 日志级别名称
 */
const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
    [LogLevel.DEBUG]: 'DEBUG',
    [LogLevel.INFO]: 'INFO',
    [LogLevel.WARN]: 'WARN',
    [LogLevel.ERROR]: 'ERROR',
    [LogLevel.SILENT]: 'SILENT'
};

/**
 * 日志配置
 */
interface LoggerConfig {
    /** 最小日志级别 */
    level: LogLevel;
    /** 是否显示时间戳 */
    timestamp: boolean;
    /** 前缀 */
    prefix: string;
}

/**
 * 全局日志配置
 */
let globalConfig: LoggerConfig = {
    level: process.env.DEBUG ? LogLevel.DEBUG : LogLevel.INFO,
    timestamp: false,
    prefix: '[MCP]'
};

/**
 * Logger 类
 */
export class Logger {
    private readonly name: string;
    private readonly config: LoggerConfig;
    
    constructor(name?: string, config?: Partial<LoggerConfig>) {
        this.name = name || '';
        this.config = { ...globalConfig, ...config };
    }
    
    /**
     * 格式化日志消息
     */
    private format(level: LogLevel, message: string, ...args: unknown[]): string {
        const parts: string[] = [];
        
        if (this.config.timestamp) {
            parts.push(new Date().toISOString());
        }
        
        parts.push(this.config.prefix);
        
        if (this.name) {
            parts.push(`[${this.name}]`);
        }
        
        parts.push(`[${LOG_LEVEL_NAMES[level]}]`);
        parts.push(message);
        
        // 处理额外参数
        if (args.length > 0) {
            const formatted = args.map(arg => {
                if (typeof arg === 'object') {
                    try {
                        return JSON.stringify(arg, null, 2);
                    } catch {
                        return String(arg);
                    }
                }
                return String(arg);
            });
            parts.push(...formatted);
        }
        
        return parts.join(' ');
    }
    
    /**
     * 检查日志级别是否应该输出
     */
    private shouldLog(level: LogLevel): boolean {
        return level >= this.config.level;
    }
    
    /**
     * 调试日志
     */
    debug(message: string, ...args: unknown[]): void {
        if (this.shouldLog(LogLevel.DEBUG)) {
            console.error(this.format(LogLevel.DEBUG, message, ...args));
        }
    }
    
    /**
     * 信息日志
     */
    info(message: string, ...args: unknown[]): void {
        if (this.shouldLog(LogLevel.INFO)) {
            console.error(this.format(LogLevel.INFO, message, ...args));
        }
    }
    
    /**
     * 简化的 log 方法（等同于 info）
     */
    log(message: string, ...args: unknown[]): void {
        this.info(message, ...args);
    }
    
    /**
     * 警告日志
     */
    warn(message: string, ...args: unknown[]): void {
        if (this.shouldLog(LogLevel.WARN)) {
            console.error(this.format(LogLevel.WARN, message, ...args));
        }
    }
    
    /**
     * 错误日志
     */
    error(message: string, ...args: unknown[]): void {
        if (this.shouldLog(LogLevel.ERROR)) {
            console.error(this.format(LogLevel.ERROR, message, ...args));
        }
    }
    
    /**
     * 创建子 Logger
     */
    child(name: string): Logger {
        const childName = this.name ? `${this.name}:${name}` : name;
        return new Logger(childName, this.config);
    }
}

/**
 * 配置全局日志
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
    globalConfig = { ...globalConfig, ...config };
}

/**
 * 设置日志级别
 */
export function setLogLevel(level: LogLevel): void {
    globalConfig.level = level;
}

/**
 * 默认 Logger 实例
 */
export const logger = new Logger();

/**
 * 创建带名称的 Logger
 */
export function createLogger(name: string): Logger {
    return new Logger(name);
}

/**
 * 工具函数：计时日志
 */
export function createTimer(logger: Logger, operation: string): () => void {
    const start = Date.now();
    logger.debug(`开始: ${operation}`);
    
    return () => {
        const duration = Date.now() - start;
        logger.debug(`完成: ${operation} (${duration}ms)`);
    };
}
